/**
 * Lightweight SVG showcase for Wheel of Fortune on the Games catalog.
 * No backend, rewards, or PIXI — visual confidence only.
 */
import "./WheelGamePreview.css";

const CX = 100;
const CY = 100;
const R_OUTER = 88;
const R_INNER = 26;

/** Default demo segments — colors only (showcase, not tied to rewards). */
const DEFAULT_SEGMENT_FILLS: readonly string[] = [
  "#5c6ac3",
  "#47c1bf",
  "#9c6ade",
  "#f49342",
  "#50b83c",
  "#de3618",
];

export interface WheelGamePreviewProps {
  /** Override segment fills (6 entries). */
  segmentFills?: readonly string[];
  pointerFill?: string;
  rimStroke?: string;
  outerRingFill?: string;
}

function polar(x: number, y: number, r: number, angleRad: number) {
  return {
    x: x + r * Math.cos(angleRad),
    y: y + r * Math.sin(angleRad),
  };
}

function segmentPath(
  index: number,
  total: number,
  outer: number,
  inner: number
): string {
  const sweep = (2 * Math.PI) / total;
  const start = -Math.PI / 2 + index * sweep;
  const end = start + sweep;
  const p1o = polar(CX, CY, outer, start);
  const p2o = polar(CX, CY, outer, end);
  const p1i = polar(CX, CY, inner, start);
  const p2i = polar(CX, CY, inner, end);
  const largeArc = sweep > Math.PI ? 1 : 0;
  return [
    `M ${p1i.x} ${p1i.y}`,
    `L ${p1o.x} ${p1o.y}`,
    `A ${outer} ${outer} 0 ${largeArc} 1 ${p2o.x} ${p2o.y}`,
    `L ${p2i.x} ${p2i.y}`,
    `A ${inner} ${inner} 0 ${largeArc} 0 ${p1i.x} ${p1i.y}`,
    "Z",
  ].join(" ");
}

export function WheelGamePreview({
  segmentFills,
  pointerFill = "#202223",
  rimStroke = "#e1e3e5",
  outerRingFill = "#f6f6f7",
}: WheelGamePreviewProps = {}) {
  const fills =
    segmentFills && segmentFills.length === DEFAULT_SEGMENT_FILLS.length
      ? [...segmentFills]
      : [...DEFAULT_SEGMENT_FILLS];
  const n = fills.length;

  return (
    <div
      className="games-wheel-preview"
      style={{
        width: "100%",
        maxWidth: 200,
        marginLeft: "auto",
        marginRight: "auto",
        aspectRatio: "200 / 220",
      }}
      aria-hidden
    >
      {/* width/height must be valid SVG lengths — never "auto" on <svg> */}
      <svg
        viewBox="0 0 200 220"
        width="100%"
        height="100%"
        role="img"
        aria-label="Wheel of Fortune preview"
      >
        {/* Pointer (fixed — does not rotate with wheel) */}
        <polygon
          points="100,6 92,22 108,22"
          fill={pointerFill}
          stroke="#f1f1f1"
          strokeWidth="1"
        />

        <g transform="translate(0, 14)">
          <circle cx={CX} cy={CY} r={R_OUTER + 4} fill={outerRingFill} stroke={rimStroke} strokeWidth="2" />

          <g className="games-wheel-preview__rotor">
            {fills.map((fill, i) => (
              <path
                key={i}
                d={segmentPath(i, n, R_OUTER, R_INNER)}
                fill={fill}
                stroke="#ffffff"
                strokeWidth="1.5"
                strokeOpacity={0.35}
              />
            ))}

            <circle cx={CX} cy={CY} r={R_INNER - 2} fill="#f1f2f4" stroke="#c9cccf" strokeWidth="2" />
            <circle cx={CX} cy={CY} r={12} fill="#ffffff" stroke="#8c9196" strokeWidth="1.5" />
            <text
              x={CX}
              y={CY}
              fill="#202223"
              fontSize="9"
              fontWeight="700"
              fontFamily="system-ui, -apple-system, sans-serif"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              SPIN
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
}
